'use client'

import { useEffect, useState } from 'react'
import { CaretDown } from '@phosphor-icons/react'
import type { ArticleHeading } from '@/lib/article-headings'

interface ArticleNavigationProps {
  headings: ArticleHeading[]
}

function NavigationLinks({ headings, activeId }: ArticleNavigationProps & { activeId: string }) {
  return (
    <ol className="border-l border-[var(--portfolio-line)]">
      {headings.map((heading) => (
        <li key={heading.id}>
          <a
            href={`#${heading.id}`}
            aria-current={activeId === heading.id ? 'location' : undefined}
            className={`-ml-px block border-l py-2 text-sm leading-snug transition-colors ${
              heading.level === 3 ? 'pl-7' : heading.level === 2 ? 'pl-5' : 'pl-3'
            } ${
              activeId === heading.id
                ? 'border-[var(--portfolio-mint)] font-semibold text-[var(--portfolio-ink)]'
                : 'border-transparent text-[var(--portfolio-muted)] hover:border-[var(--portfolio-line)] hover:text-[var(--portfolio-ink)]'
            }`}
          >
            {heading.text}
          </a>
        </li>
      ))}
    </ol>
  )
}

export default function ArticleNavigation({ headings }: ArticleNavigationProps) {
  const [activeId, setActiveId] = useState(headings[0]?.id || '')

  useEffect(() => {
    if (headings.length === 0) return

    const elements = headings
      .map(({ id }) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null)

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]?.target.id) setActiveId(visible[0].target.id)
      },
      { rootMargin: '-18% 0px -68% 0px', threshold: 0 },
    )

    elements.forEach((element) => observer.observe(element))
    return () => observer.disconnect()
  }, [headings])

  if (headings.length === 0) return null

  return (
    <>
      <details className="group mb-10 rounded-[8px] border border-[var(--portfolio-line)] bg-[var(--portfolio-surface)] lg:hidden">
        <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold text-[var(--portfolio-ink)] [&::-webkit-details-marker]:hidden">
          On this page
          <CaretDown size={16} className="text-[var(--portfolio-muted)] transition-transform group-open:rotate-180" aria-hidden="true" />
        </summary>
        <nav aria-label="Table of contents" className="border-t border-[var(--portfolio-line)] px-4 py-4">
          <NavigationLinks headings={headings} activeId={activeId} />
        </nav>
      </details>

      <aside className="hidden lg:col-start-2 lg:row-start-1 lg:block">
        <div className="sticky top-28 max-h-[calc(100dvh-8rem)] overflow-y-auto pr-2">
          <p className="mb-4 text-sm font-semibold text-[var(--portfolio-ink)]">On this page</p>
          <nav aria-label="Table of contents">
            <NavigationLinks headings={headings} activeId={activeId} />
          </nav>
        </div>
      </aside>
    </>
  )
}
